/**
 * 一键反馈（ADR-0012）：先截下当前页面，再写一句话，连同页面地址、屏幕尺寸、最近的报错一起传回 Mac。
 */
import { useRouterState } from "@tanstack/react-router";
import { toBlob } from "html-to-image";
import { MessageSquareWarning } from "lucide-react";
import { useRef, useState } from "react";
import { errorMessage } from "@/shared/api/client";
import { sendFeedback } from "@/shared/api/gen/sdk.gen";
import { useUi } from "@/shared/scene";
import { recentErrors, viewport } from "@/shared/telemetry";
import { Button, ErrorNote, Spinner, TextArea } from "@/shared/ui";

export function FeedbackButton() {
  const dialog = useRef<HTMLDialogElement>(null);
  const route = useRouterState({ select: (s) => s.location.href });
  const [shot, setShot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [attach, setAttach] = useState(true);
  const [text, setText] = useState("");
  const [state, setState] = useState<"idle" | "capturing" | "sending" | "sent">("idle");
  const [error, setError] = useState<unknown>(null);

  const open = async () => {
    setState("capturing");
    setError(null);
    setText("");
    try {
      const blob = await toBlob(document.body, {
        pixelRatio: Math.min(window.devicePixelRatio, 2),
        cacheBust: true,
      });
      setShot(blob);
      setPreview(blob ? URL.createObjectURL(blob) : null);
    } catch {
      setShot(null);
      setPreview(null);
    }
    setState("idle");
    dialog.current?.showModal();
  };

  const close = () => {
    dialog.current?.close();
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  };

  const send = async () => {
    setState("sending");
    setError(null);
    try {
      await sendFeedback({
        body: {
          text,
          route,
          meta: JSON.stringify({
            viewport: viewport(),
            user_agent: navigator.userAgent,
            scene: useUi.getState().scene,
            errors: recentErrors(),
          }),
          ...(attach && shot ? { screenshot: shot } : {}),
        },
      });
      setState("sent");
      setTimeout(close, 900);
    } catch (e) {
      setError(e);
      setState("idle");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void open()}
        aria-label="反馈"
        title="反馈：截下当前页面，写一句话传回 Mac"
        className="grid size-8 shrink-0 place-items-center rounded-full border border-border text-text-2 hover:text-text"
      >
        {state === "capturing" ? <Spinner /> : <MessageSquareWarning className="size-4" />}
      </button>
      <dialog
        ref={dialog}
        aria-label="反馈"
        onClose={() => setState("idle")}
        className="mx-auto mt-[10vh] w-[min(480px,calc(100vw-32px))] rounded-l border border-border bg-surface p-0 text-text shadow-soft backdrop:bg-text/25"
      >
        <form
          className="flex flex-col gap-3 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <div className="font-semibold">反馈给 Mac</div>
          <TextArea
            aria-label="反馈内容"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="哪里不对、想要什么？一句话就行"
          />
          {preview ? (
            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={attach}
                onChange={(e) => setAttach(e.target.checked)}
                className="mt-1"
              />
              <span className="flex flex-col gap-1.5">
                附上这一页的截图
                <img
                  src={preview}
                  alt="当前页面截图"
                  className="max-h-40 rounded-s border border-border object-contain"
                />
              </span>
            </label>
          ) : null}
          <div className="text-[12px] text-text-3">
            还会带上页面地址、屏幕尺寸和最近的报错，只存在你的 Mac 上。
          </div>
          {error ? <ErrorNote>{errorMessage(error)}</ErrorNote> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              取消
            </Button>
            <Button
              type="submit"
              variant="primary"
              busy={state === "sending"}
              disabled={state === "sent" || (!text.trim() && !(attach && shot))}
            >
              {state === "sent" ? "已发送" : "发送"}
            </Button>
          </div>
        </form>
      </dialog>
    </>
  );
}
